import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { stages, DEPARTMENT_NAMES, type Stage } from '../data/stages-data';

const STAGE_COLORS: Record<number, string> = {
  0: 'bg-gray-500',
  1: 'bg-red-500',
  2: 'bg-orange-500',
  3: 'bg-amber-500',
  4: 'bg-yellow-500',
  5: 'bg-lime-500',
  6: 'bg-green-500',
  7: 'bg-teal-500',
  8: 'bg-cyan-500',
  9: 'bg-blue-500',
};

const STAGE_BORDER_COLORS: Record<number, string> = {
  0: 'border-gray-300',
  1: 'border-red-300',
  2: 'border-orange-300',
  3: 'border-amber-300',
  4: 'border-yellow-300',
  5: 'border-lime-300',
  6: 'border-green-300',
  7: 'border-teal-300',
  8: 'border-cyan-300',
  9: 'border-blue-300',
};

const DEPARTMENT_ICONS: Record<string, string> = {
  Product: '📦',
  Marketing: '📣',
  Sales: '🤝',
  'Customer Service': '🎧',
  IT: '💻',
  Recruiting: '🔍',
  'Human Resources': '👥',
  Finance: '💰',
  'Bottom Line': '📊',
};

function DeptCell({ dept }: { dept: Stage['departments'][number] | undefined }) {
  if (!dept) return <td className="p-2 text-xs text-muted-foreground border">—</td>;

  return (
    <td className="p-2 text-xs border align-top min-w-[180px] max-w-[240px]">
      <div className="space-y-2">
        <div>
          <span className="text-[10px] font-medium uppercase tracking-wider text-amber-600 dark:text-amber-400">Constraint</span>
          <p>{dept.constraint}</p>
        </div>
        <div>
          <span className="text-[10px] font-medium uppercase tracking-wider text-red-600 dark:text-red-400">Pains</span>
          <ul className="space-y-0.5 list-disc pl-3">
            {dept.pain.map((p, i) => (
              <li key={i} className="text-muted-foreground">{p}</li>
            ))}
          </ul>
        </div>
        <div>
          <span className="text-[10px] font-medium uppercase tracking-wider text-green-600 dark:text-green-400">Solution</span>
          <p className="font-medium text-green-700 dark:text-green-400">{dept.solution}</p>
        </div>
      </div>
    </td>
  );
}

export function StagesTable() {
  const [expandedDepts, setExpandedDepts] = useState<Set<string>>(new Set(DEPARTMENT_NAMES));
  const [hiddenStages, setHiddenStages] = useState<Set<number>>(() => {
    try {
      const saved = localStorage.getItem('atek-stages-hidden');
      return saved ? new Set(JSON.parse(saved) as number[]) : new Set();
    } catch {
      return new Set();
    }
  });

  const toggleDept = (name: string) => {
    setExpandedDepts((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const hideStage = (num: number) => {
    setHiddenStages((prev) => {
      const next = new Set(prev).add(num);
      localStorage.setItem('atek-stages-hidden', JSON.stringify([...next]));
      return next;
    });
  };

  const showStage = (num: number) => {
    setHiddenStages((prev) => {
      const next = new Set(prev);
      next.delete(num);
      localStorage.setItem('atek-stages-hidden', JSON.stringify([...next]));
      return next;
    });
  };

  const visibleStages = stages.filter((s) => !hiddenStages.has(s.number));

  return (
    <div className="space-y-2">
      {hiddenStages.size > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Hidden:</span>
          {stages
            .filter((s) => hiddenStages.has(s.number))
            .map((s) => (
              <button
                key={s.number}
                onClick={() => showStage(s.number)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-border hover:bg-muted/50 transition-colors"
              >
                <Badge className={cn('text-white text-[10px] px-1.5 py-0', STAGE_COLORS[s.number])}>
                  {s.number}
                </Badge>
                <span>{s.name}</span>
              </button>
            ))}
        </div>
      )}
      <div className="border rounded-lg overflow-auto max-h-[calc(100vh-220px)]">
        <table className="border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-background">
            {/* Stage number + name headers */}
            <tr>
              <th className="sticky left-0 z-20 bg-background border p-2 text-left min-w-[140px]">
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Department</span>
              </th>
              {visibleStages.map((s) => (
                <th
                  key={s.number}
                  className={cn('border p-2 text-center min-w-[180px] relative group', STAGE_BORDER_COLORS[s.number])}
                >
                  <button
                    onClick={() => hideStage(s.number)}
                    className="absolute top-1 right-1 p-0.5 rounded hover:bg-muted/80 opacity-0 group-hover:opacity-100 transition-opacity"
                    title={`Hide stage ${s.number}`}
                  >
                    <X className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                  <Badge className={cn('text-white text-xs', STAGE_COLORS[s.number])}>
                    {s.number}
                  </Badge>
                  <div className="font-semibold mt-1">{s.name}</div>
                  <div className="text-[10px] text-muted-foreground font-normal">{s.headcount} people</div>
                </th>
              ))}
            </tr>
            {/* Role + Leadership row */}
            <tr className="bg-muted/30">
              <td className="sticky left-0 z-20 bg-muted/30 border p-2 text-xs font-medium text-muted-foreground">
                Your Role
              </td>
              {visibleStages.map((s) => (
                <td key={s.number} className="border p-2 text-xs text-center font-medium">
                  {s.role}
                </td>
              ))}
            </tr>
            <tr className="bg-muted/20">
              <td className="sticky left-0 z-20 bg-muted/20 border p-2 text-xs font-medium text-muted-foreground">
                Leadership
              </td>
              {visibleStages.map((s) => (
                <td key={s.number} className="border p-2 text-xs text-center">
                  {s.leadershipStructure}
                </td>
              ))}
            </tr>
          </thead>

          <tbody>
            {DEPARTMENT_NAMES.map((deptName) => {
              const isExpanded = expandedDepts.has(deptName);
              return (
                <tr key={deptName} className={cn(!isExpanded && 'opacity-60')}>
                  <td className="sticky left-0 z-10 bg-background border p-2 min-w-[140px]">
                    <button
                      onClick={() => toggleDept(deptName)}
                      className="flex items-center gap-1.5 w-full text-left hover:text-primary transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      )}
                      <span className="text-sm">{DEPARTMENT_ICONS[deptName]}</span>
                      <span className="text-xs font-medium truncate">{deptName}</span>
                    </button>
                  </td>
                  {visibleStages.map((s) => {
                    const dept = s.departments.find((d) => d.name === deptName);
                    if (!isExpanded) {
                      return (
                        <td key={s.number} className="border p-2 text-xs text-muted-foreground truncate max-w-[180px]">
                          {dept?.constraint.slice(0, 40)}…
                        </td>
                      );
                    }
                    return <DeptCell key={s.number} dept={dept} />;
                  })}
                </tr>
              );
            })}

            {/* Comp package row */}
            <tr className="bg-muted/10">
              <td className="sticky left-0 z-10 bg-muted/10 border p-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">💰</span>
                  <span className="text-xs font-medium">Comp Package</span>
                </div>
              </td>
              {visibleStages.map((s) => (
                <td key={s.number} className="border p-2 text-xs align-top max-w-[240px]">
                  {s.compPackage.philosophy ? (
                    <p className="text-muted-foreground">{s.compPackage.philosophy}</p>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
              ))}
            </tr>

            {/* Companies at this stage row */}
            <tr className="bg-muted/20">
              <td className="sticky left-0 z-10 bg-muted/20 border p-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm">🏢</span>
                  <span className="text-xs font-medium">Companies</span>
                </div>
              </td>
              {visibleStages.map((s) => (
                <td key={s.number} className="border p-2 text-xs text-center text-muted-foreground">
                  {s.qtyCompanies}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
